import React, { useState, useEffect, useRef } from 'react';
import { bool, func, string } from 'prop-types';
import classNames from 'classnames';
import { FaHeart, FaRegHeart } from 'react-icons/fa';

import css from './FavoriteButton.module.css';

const FavoriteButton = React.forwardRef((props, ref) => {
    const { className, rootClassName, isFavorited, isLoading, onClick, disabled } = props;
    const [showAnimation, setShowAnimation] = useState(false);
    const [particles, setParticles] = useState([]);
    const [userInitiated, setUserInitiated] = useState(false);
    const prevIsLoadingRef = useRef(isLoading);

    const classes = classNames(rootClassName || css.root, className, {
        [css.favorited]: isFavorited,
        [css.disabled]: disabled || isLoading,
        [css.loading]: isLoading,
    });

    useEffect(() => {
        // Trigger animation if:
        // 1. The user clicked the button (userInitiated)
        // 2. Loading has finished (!isLoading)
        // 3. The item is now favorited (isFavorited)

        // We checking for ALL conditions simultaneously to handle race conditions
        // where isLoading might flip before isFavorited updates.
        if (userInitiated && !isLoading && isFavorited) {
            console.log('Animation Success: Triggering Hearts');

            // Success: Trigger animation
            const newParticles = Array.from({ length: 2 }).map((_, i) => ({
                id: Date.now() + i, // Unique IDs to ensure re-render
                // Tighter spread to keep them closer together
                tx: Math.floor(Math.random() * 20) - 40, // -40 to -20
                ty: Math.floor(Math.random() * 15) - 10,  // -10 to 5
            }));
            setParticles(newParticles);
            setShowAnimation(true);
            setTimeout(() => setShowAnimation(false), 2000);

            // Only reset userInitiated on SUCCESS or explicit logic reset
            setUserInitiated(false);
        }

        prevIsLoadingRef.current = isLoading;
    }, [isLoading, isFavorited, userInitiated]);

    const handleClick = e => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled && !isLoading && onClick) {
            if (!isFavorited) {
                setUserInitiated(true);
            }
            onClick();
        }
    };

    return (
        <button
            type="button"
            ref={ref}
            className={classes}
            onClick={handleClick}
            disabled={disabled || isLoading}
            aria-label={isFavorited ? 'Remove from wishlist' : 'Add to wishlist'}
        >
            {isLoading ? (
                <span className={css.spinner} />
            ) : isFavorited ? (
                <FaHeart className={css.iconFilled} />
            ) : (
                <FaRegHeart className={css.iconOutline} />
            )}
            {showAnimation && (
                <div className={css.animationContainer}>
                    {particles.map(p => (
                        <FaHeart
                            key={p.id}
                            className={css.particle}
                            style={{ '--tx': `${p.tx}px`, '--ty': `${p.ty}px` }}
                        />
                    ))}
                </div>
            )}
        </button>
    );
});

FavoriteButton.defaultProps = {
    className: null,
    rootClassName: null,
    isFavorited: false,
    isLoading: false,
    disabled: false,
};

FavoriteButton.propTypes = {
    className: string,
    rootClassName: string,
    isFavorited: bool,
    isLoading: bool,
    onClick: func.isRequired,
    disabled: bool,
};

export default FavoriteButton;
